import { useNavigate } from "react-router-dom";
import { useRef, useEffect } from "react";
import { accessCardState } from "../../../stores/accessCardStore";
import TWButton from "../../../components/TWButton";
import WeddnLoader from "../../../components/WeddnLoader";
import { MessagingStore, WeddingReminderStore } from "../../Guests/Messaging/types";
import { MailType } from "../../../types/mailcredit";
import { Routes } from "../../../routes/baseRoutes";
import { useSnapshot } from "valtio";

interface GenerationStatesProps {
  status: "idle" | "loading" | "success" | "error";
  leftPanelRef?: React.RefObject<HTMLDivElement>;
}

const GenerationStates = ({ status, leftPanelRef }: GenerationStatesProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { generation, generationError, generatedGuestIds } =
    useSnapshot(accessCardState);

  // Handle clicks on left panel only
  useEffect(() => {
    const handleLeftPanelClick = (event: MouseEvent) => {
      if (!event.target || !leftPanelRef?.current) return;

      // Only reset if click is within the left sidebar
      if (leftPanelRef.current.contains(event.target as Node)) {
        accessCardState.resetGenerationStatus();
      }
    };

    // Only add listener when in success state
    if (status === "success") {
      document.addEventListener("mousedown", handleLeftPanelClick);
      return () => {
        document.removeEventListener("mousedown", handleLeftPanelClick);
      };
    }
  }, [status, leftPanelRef]);

  if (status === "loading") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 lg:bg-transparent overflow-y-auto">
        <WeddnLoader message="Generating access cards..." variant="contained" />
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 lg:bg-transparent overflow-y-auto p-8">
        <div ref={containerRef} className="text-center max-w-sm">
          {/* Success Icon */}
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2 uppercase tracking-wide">
            CARDS GENERATED
          </h3>
          <p className="text-sm text-gray-600 mb-8">
            {generation?.generated ?? generatedGuestIds.length} access cards have
            been generated and are ready to send.
          </p>

          {/* Action Button */}
          <div className="flex justify-center">
            <TWButton
              onClick={() => {
                const guestIDs = [...generatedGuestIds];
                MessagingStore.mailType = MailType.AccessCard;
                MessagingStore.prefilledGuestIDs = guestIDs;
                WeddingReminderStore[MailType.AccessCard].guests = guestIDs;
                WeddingReminderStore[MailType.AccessCard].sendToAll = false;
                accessCardState.resetGenerationStatus();
                navigate(Routes.guestsMessaging);
              }}
              variant="primary"
              size="lg"
            >
              SEND TO GUESTS
            </TWButton>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 p-8">
        <div className="max-w-sm text-center">
          <h3 className="mb-2 text-lg font-semibold uppercase tracking-wide text-gray-900">
            GENERATION FAILED
          </h3>
          <p className="mb-6 text-sm text-gray-600">
            {generationError ||
              "We couldn't generate the access cards. Please try again."}
          </p>
          <TWButton onClick={() => accessCardState.resetGenerationStatus()}>
            BACK TO SUMMARY
          </TWButton>
        </div>
      </div>
    );
  }

  // Idle state shows the normal preview.
  return null;
};

export default GenerationStates;
